import { PILL_COLORS } from "components/ui/Pill";
import { formatDateOfBirth } from "utils/dateFormatter";

// How the duplicate check describes a candidate. These are the only three values
// `PatientService.classifyDuplicateMatch` can return, and all three are computed from
// date of birth equality plus fuzzy first-name/surname scores — nothing else. There is
// no identifier (PPSN / GMS / IHI) matching behind any of them, so none of the wording
// below may imply one.
export const DUPLICATE_MATCH_TYPE = {
  exact: "EXACT",
  likely: "LIKELY",
  possible: "POSSIBLE",
};

// Both the first name and the surname matched outright and the date of birth is the
// same; a close name match on a shared date of birth; or a name match on its own with
// no confirmed date of birth. The explanation spells out what was compared, because
// "same name and date of birth" is routinely legitimate — twins share a birthday and
// siblings share a surname — and an admin deciding whether to carry on needs to know
// that is all the system checked.
const MATCH_STRENGTHS = {
  [DUPLICATE_MATCH_TYPE.exact]: {
    label: "Exact name and date of birth",
    explanation:
      "First name, surname and date of birth all match this record.",
    pillColor: PILL_COLORS.statusError,
  },
  [DUPLICATE_MATCH_TYPE.likely]: {
    label: "Similar name, same date of birth",
    explanation:
      "The date of birth matches and at least one name is a close match.",
    pillColor: PILL_COLORS.statusWarning,
  },
  [DUPLICATE_MATCH_TYPE.possible]: {
    label: "Similar name only",
    explanation:
      "The names are similar. The date of birth was not confirmed as a match.",
    pillColor: PILL_COLORS.statusNoStatus,
  },
};

const UNKNOWN_MATCH_STRENGTH = {
  label: "Possible match",
  explanation: "This record resembles the details being entered.",
  pillColor: PILL_COLORS.statusNoStatus,
};

// Strongest first: the record most likely to be the same person should be the one read
// first, whatever order the backend happened to return them in.
const MATCH_TYPE_ORDER = [
  DUPLICATE_MATCH_TYPE.exact,
  DUPLICATE_MATCH_TYPE.likely,
  DUPLICATE_MATCH_TYPE.possible,
];

// A match type the backend has not sent before sorts last rather than first, so a new
// value added server-side cannot quietly outrank an exact match.
const rankMatchType = ({ matchType }) => {
  const rank = MATCH_TYPE_ORDER.indexOf(matchType);

  return rank === -1 ? MATCH_TYPE_ORDER.length : rank;
};

export const describeMatchStrength = (matchType) =>
  MATCH_STRENGTHS[matchType] ?? UNKNOWN_MATCH_STRENGTH;

// The check only ever compares first name, surname and date of birth — those are the
// only parameters the endpoint takes. It also ignores a name shorter than two
// characters, so asking before anything usable has been typed just spends a request to
// be told nothing.
const MINIMUM_NAME_LENGTH = 2;

export const shouldRunDuplicateCheck = ({
  firstName,
  surname,
  dateOfBirth,
} = {}) => {
  const hasFirstName = (firstName ?? "").trim().length >= MINIMUM_NAME_LENGTH;
  const hasSurname = (surname ?? "").trim().length >= MINIMUM_NAME_LENGTH;

  return hasFirstName || hasSurname || !!dateOfBirth;
};

const buildName = (candidate) =>
  [candidate.firstName, candidate.surname].filter(Boolean).join(" ").trim();

// The DTO carries id, both names, date of birth, address line 1 and the match type —
// and nothing else. In particular it carries no identifier, so a candidate row can show
// the name, the date of birth and one line of address, and must not claim more.
export const toDuplicateCandidates = (matches) => {
  if (!Array.isArray(matches)) return [];

  return matches
    .filter((match) => match?.id != null)
    .map((match) => ({
      id: match.id,
      name: buildName(match) || "Unnamed patient",
      dateOfBirth: match.dateOfBirth
        ? formatDateOfBirth(match.dateOfBirth)
        : "",
      addressLine1: match.addressLine1 || "",
      strength: describeMatchStrength(match.matchType),
      matchType: match.matchType,
    }))
    .sort((first, second) => rankMatchType(first) - rankMatchType(second));
};
